// Competitions pages — pure helpers (TP-CompetitionPage build).
// Shapes = the merged BE V1 API (dashboard/utils/competitions.py,
// utils/standings.py build_standings, utils/cups.py build_cup_bracket) — see
// TP-CompetitionPage/build/CONTRACTS.md. Names come from the BE resolver
// (display name is delivered as `name`) — the FE NEVER applies overrides.
// "Empty renders nothing" — helpers return null/[] for missing data.

import { canonicalPromotion } from "../../components/competitions/shared/competitionsZones";

// Monogram for card/hero logo fallback (spec: missing logo → monogram).
export const competitionMonogram = (comp) => {
  const short = (comp?.short_name || "").trim();
  if (short) return short.slice(0, 3).toUpperCase();
  const name = (comp?.name || comp?.display_name || "").trim();
  if (!name) return "";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return parts
    .slice(0, 3)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
};

export const competitionName = (comp) =>
  comp?.name || comp?.display_name || comp?.short_name || null;

// ── Browse ─────────────────────────────────────────────────────────────────
// GET /api/competitions/ is a flat peek-paginated list
// ({results:[{id,name,short_name,logo,type,kind,country:{id,name,logo},
// cur_season:{id,year}}], pagination:{has_more,...}}). The browse page pages
// through it and groups client-side: International (no country) first, then
// regions alphabetically, competitions in delivered order.
export const groupCompetitions = (competitions) => {
  const list = Array.isArray(competitions) ? competitions : [];
  const byRegion = new Map();
  list.forEach((comp) => {
    if (!competitionName(comp)) return;
    const countryName = comp.country?.name || comp.country_name || "";
    const label = countryName || "International";
    if (!byRegion.has(label)) byRegion.set(label, []);
    byRegion.get(label).push(comp);
  });
  const labels = Array.from(byRegion.keys());
  labels.sort((a, b) => {
    if (a === "International") return -1;
    if (b === "International") return 1;
    return a.localeCompare(b);
  });
  return labels.map((label) => ({
    key: label.toLowerCase(),
    label,
    count: byRegion.get(label).length,
    competitions: byRegion.get(label),
  }));
};

// Case-insensitive browse filter over name/short name/country. Regions
// without a match are absent (B2).
export const filterSections = (sections, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return sections;
  return sections
    .map((s) => ({
      ...s,
      competitions: s.competitions.filter((c) => {
        const hay = [c.name, c.display_name, c.short_name, c.country?.name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      }),
    }))
    .filter((s) => s.competitions.length > 0);
};

export const countCompetitions = (sections) =>
  sections.reduce((n, s) => n + s.competitions.length, 0);

// ── Formats (§22) ──────────────────────────────────────────────────────────
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
export const formatMatchDate = (unixSeconds) => {
  const ts = Number(unixSeconds);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  const d = new Date(ts * 1000);
  return `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}`;
};
export const formatMatchTime = (unixSeconds, tbd) => {
  if (tbd) return "TBD";
  const ts = Number(unixSeconds);
  if (!Number.isFinite(ts) || ts <= 0) return null;
  const d = new Date(ts * 1000);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
};

// ── Section identifiers (K1 §6.2) ──────────────────────────────────────────
// V1 renders the first four; BEST_XI/HISTORY exist as constants only (V2 —
// designed, not built) and are deliberately NOT in V1_SECTIONS.
export const SECTIONS = {
  OVERVIEW: "overview",
  STANDINGS: "standings",
  SCHEDULE: "schedule",
  STATS: "stats",
  BEST_XI: "best-xi", // V2 — not rendered
  HISTORY: "history", // V2 — not rendered
};
export const V1_SECTIONS = [
  SECTIONS.OVERVIEW,
  SECTIONS.STANDINGS,
  SECTIONS.SCHEDULE,
  SECTIONS.STATS,
];

// ── Schedule mapping: BE /matches/ → CompetitionScheduleList rows ─────────
// BE match payload (_match_payload): {id, status_id, match_time, tbd, home,
// away, score:{home,away,ot_home?,ot_away?,aet,penalty?}, winner, agg_score,
// related_id, round:{round_num,...}}. Headline score for played matches =
// after extra time when it happened (spec: pens example keeps the after-ET
// "4 : 4"), regular time otherwise. Empty score object = not finished →
// kickoff time.
//
// Leg meta (§8 "1st leg"/"2nd leg"): `related_id` links the two legs of a
// double round (ENDPOINTS.md §7 verbatim: "The match id of the other round in
// the double round"). With `timesById` (built by flattenSchedule over the WHOLE
// season payload — the sibling leg may sit in another round/stage bucket) the
// earlier kickoff is leg 1, the later leg 2. No sibling in the payload or equal
// times ⇒ null (never invented).
const legOf = (match, timesById) => {
  const rid = match?.related_id;
  if (!rid || !(timesById instanceof Map) || !timesById.has(rid)) return null;
  const own = Number(match?.match_time);
  const other = Number(timesById.get(rid));
  if (!Number.isFinite(own) || !Number.isFinite(other) || own === other) {
    return null;
  }
  return own < other ? 1 : 2;
};

export const mapMatchToScheduleRow = (match, timesById) => {
  const score = match?.score || {};
  const finished = score.home != null && score.away != null;
  const agg =
    Array.isArray(match?.agg_score) && match.agg_score.length === 2
      ? { home: match.agg_score[0], away: match.agg_score[1] }
      : null;
  return {
    id: match.id,
    date: match.match_time,
    tbd: Boolean(match.tbd),
    home: match.home || {},
    away: match.away || {},
    homeScore: finished ? (score.ot_home ?? score.home) : null,
    awayScore: finished ? (score.ot_away ?? score.away) : null,
    hasOvertime: Boolean(score.aet),
    penalties: score.penalty || null,
    agg,
    leg: legOf(match, timesById),
    // §8 venue column: pure pass-through — rendered only when the BE ever
    // delivers a `venue` string on the match payload (today it does not;
    // BE follow-up, QA-CODE §4.2). Never derived, never invented.
    venue: typeof match?.venue === "string" && match.venue ? match.venue : null,
  };
};

// Flatten the stage/round grouping of /matches/ into { rounds, byRound,
// timesById }. League pages navigate by round number across ALL stages (round
// labels stay "Round {n}"); matches inside a round keep delivered order.
// `timesById` (match id → match_time over the whole payload) feeds the
// related_id-based leg derivation in mapMatchToScheduleRow.
//
// Unnumbered rounds (BE PR #411: `round_num` is `null` for cup stages — the
// provider uses 0/None there, the BE now serves null): those matches are NOT
// dropped (dropping them blanked the whole Schedule section on pure-cup
// seasons). They collect into ONE bucket keyed by the UNNUMBERED_ROUND
// sentinel, sorted BEHIND the numbered rounds (mirrors the BE's own bucket
// ordering). Its navigator title = the stage/round label when the payload
// carries exactly one, else the neutral "All fixtures" (rendered by
// CompetitionScheduleList's labelOf — any non-numeric round value).
export const UNNUMBERED_ROUND = "unnumbered";

export const flattenSchedule = (matchesPayload) => {
  const stages = Array.isArray(matchesPayload?.stages)
    ? matchesPayload.stages
    : [];
  const byRound = new Map();
  const timesById = new Map();
  const bucketLabels = new Set();
  stages.forEach((stageEntry) => {
    (stageEntry?.rounds || []).forEach((r) => {
      const num = r?.round_num;
      const key = num == null ? UNNUMBERED_ROUND : num;
      if (num == null) {
        // Candidate bucket title: round label first, then the stage name —
        // never invented, only what the BE delivers.
        const label = String(r?.label || stageEntry?.stage?.name || "").trim();
        if (label) bucketLabels.add(label);
      }
      if (!byRound.has(key)) byRound.set(key, []);
      (r.matches || []).forEach((m) => {
        byRound.get(key).push(m);
        if (m?.id != null) timesById.set(m.id, m.match_time);
      });
    });
  });
  const rounds = Array.from(byRound.keys())
    .filter((k) => k !== UNNUMBERED_ROUND)
    .sort((a, b) => a - b)
    .map((n) => ({ round: n }));
  if (byRound.has(UNNUMBERED_ROUND)) {
    rounds.push({
      round: UNNUMBERED_ROUND,
      // One unambiguous BE label → use it; several/none → neutral title
      // (labelOf falls back to "All fixtures" when label is null).
      label: bucketLabels.size === 1 ? Array.from(bucketLabels)[0] : null,
    });
  }
  return { rounds, byRound, timesById };
};

// The round the navigator opens on: the first round with an unplayed match
// (nearest actuality), else the last played round.
export const defaultRound = (rounds, byRound) => {
  for (const r of rounds) {
    const matches = byRound.get(r.round) || [];
    if (matches.some((m) => !(m?.score && m.score.home != null))) return r.round;
  }
  return rounds.length ? rounds[rounds.length - 1].round : null;
};

// ── Bracket mapping: BE /api/cups/<id>/ ties → CompetitionBracket rounds ──
// Cups tie (utils/cups.py): {id, status: 'played'|'scheduled'|'twoleg'|'tbd',
// home, away, score:{hs,as,aet,winner,hp?,ap?} (single-leg only),
// legs:[{hs,as}|null, {hs,as}|null] + top-level winner (two-leg only),
// shootout:{hp,ap}?, date}.
//
// The component contract (CompetitionBracket / shared/competitionsFormat,
// test-pinned in __tests__/CompetitionBracket.test.js) is ARRAY-shaped:
// legs = [[hs,as],…], aggregate = [h,a]; deriveWinner reads matchup.winner.
// The winner sits TOP-LEVEL on two-leg ties (cups.py _two_leg_tie) and in
// score.winner on single-leg played ties (_played_tie) — read both (QA D1).
const mapTieToMatchup = (tie) => {
  const score = tie?.score || {};
  const rawLegs = Array.isArray(tie?.legs) ? tie.legs : [];
  const legs = rawLegs
    .filter((l) => l && l.hs != null && l.as != null)
    .map((l) => [l.hs, l.as]);
  // Aggregate only once EVERY leg is played (cups.py sets legs[i] = null for
  // an unplayed leg and decides the winner only with both legs) — a half-tie
  // sum is not an aggregate.
  const aggregate =
    legs.length > 1 && legs.length === rawLegs.length
      ? legs.reduce((acc, l) => [acc[0] + l[0], acc[1] + l[1]], [0, 0])
      : null;
  const penalties =
    tie?.shootout && tie.shootout.hp != null
      ? { home: tie.shootout.hp, away: tie.shootout.ap }
      : score.hp != null && score.ap != null
        ? { home: score.hp, away: score.ap }
        : null;
  return {
    id: tie?.id,
    home: tie?.home || null,
    away: tie?.away || null,
    homeScore: tie?.status === "played" ? (score.hs ?? null) : null,
    awayScore: tie?.status === "played" ? (score.as ?? null) : null,
    winner: tie?.winner || score.winner || null,
    legs: legs.length ? legs : null,
    aggregate,
    penalties,
  };
};

// K9 round-indicator short labels from the BE's stable round keys
// (cups.py ROUND_KEYS_BY_TIE_COUNT). Prelim keys ("prelim", "prelim2", …)
// have no spec'd short form — no abbr, the component falls back to the
// full round title.
const ROUND_ABBRS = {
  f: "F",
  sf: "SF",
  qf: "QF",
  r16: "R16",
  r32: "R32",
  r64: "R64",
};

// Round titles from the BE's STABLE keys (cups.py ROUND_KEYS_BY_TIE_COUNT,
// guard-tested since BE PR #411) instead of the BE `label`, which is
// test-pinned GERMAN ("Achtelfinale", intent per PR #411 F-12). EN base
// strings here; CompetitionBracket runs them through trAny(), so K10's DE
// ("Achtelfinale/Halbfinale/Finale") comes from the static bundles. Unknown
// keys (prelimN, rN, future values) fall back to the BE label VERBATIM —
// nothing is ever lost, worst case is the old behaviour.
const CUP_ROUND_TITLES = {
  f: "Final",
  sf: "Semi-final",
  qf: "Quarter-final",
  r16: "Round of 16",
  r32: "Round of 32",
  r64: "Round of 64",
};

// EN title for a cup round from its STABLE key, BE label as verbatim
// fallback (same contract as mapCupToBracketRounds). Shared with the
// club-matches cup-path card so German BE labels never leak into EN UI.
export const cupRoundTitle = (round) =>
  CUP_ROUND_TITLES[round?.key] || round?.label || "";

// WM-Bracket-Fix (Marin 2026-08-20): the BE now ships the third-place tie
// as an ADDITIVE detached `thirdPlace` key (never a ladder round) — mapped
// into the component's existing thirdPlace slot.
export const mapCupThirdPlace = (cupPayload) =>
  cupPayload?.thirdPlace ? mapTieToMatchup(cupPayload.thirdPlace) : null;

export const mapCupToBracketRounds = (cupPayload) => {
  const rounds = Array.isArray(cupPayload?.rounds) ? cupPayload.rounds : [];
  return rounds
    .map((r) => ({
      id: r?.key,
      name: CUP_ROUND_TITLES[r?.key] || r?.label || "",
      abbr: ROUND_ABBRS[r?.key] || null,
      matchups: (r?.ties || []).map(mapTieToMatchup),
    }))
    .filter((r) => r.matchups.length > 0);
};

// ── Leaderboards mapping: BE /leaderboards/ → CompetitionStatsSection ─────
// BE boards: top_scorers / top_assists / most_minutes / top_rated; row =
// {player:{id,name,logo,position,nationality,wyscout_id?}, teams:[{id,name,
// logo,...}], goals, assists, minutes_played, matches_played, rating}.
// `wyscout_id` (BE PR #410) is the platform id profilePathFor navigates on —
// it MUST survive the mapping or every stats row stays inert (QA-VISUAL D-V1).
// The club object is passed through whole, so team ids already survive.
const toStatsPlayer = (player) => ({
  id: player?.id,
  name: player?.name,
  photo: player?.logo || undefined,
  position: player?.position || undefined,
  wyscout_id: player?.wyscout_id ?? undefined,
  platform_player_id: player?.platform_player_id ?? undefined,
});

const toPlayerRow = (row, index, valueKey) => ({
  rank: index + 1,
  player: toStatsPlayer(row?.player),
  club: row?.teams?.[0] || undefined,
  context: row?.matches_played || undefined,
  value: row?.[valueKey],
});

export const mapLeaderboardsToStatsProps = (payload) => {
  const boards = payload?.boards || {};
  const topScorers = Array.isArray(boards.top_scorers) ? boards.top_scorers : [];
  const topAssists = Array.isArray(boards.top_assists) ? boards.top_assists : [];
  const topRated = Array.isArray(boards.top_rated) ? boards.top_rated : [];
  const ratingRows = topRated
    .filter((r) => r?.rating != null && Number(r.rating) > 0)
    .map((row, i) => ({
      rank: i + 1,
      player: toStatsPlayer(row?.player),
      club: row?.teams?.[0] || undefined,
      matches: row?.matches_played || undefined,
      minutes: row?.minutes_played || undefined,
      rating: row?.rating,
    }));
  return {
    rating: ratingRows.length ? { rows: ratingRows } : null,
    playerBoards: {
      goals: topScorers
        .filter((r) => Number(r?.goals) > 0)
        .map((r, i) => toPlayerRow(r, i, "goals")),
      assists: topAssists
        .filter((r) => Number(r?.assists) > 0)
        .map((r, i) => toPlayerRow(r, i, "assists")),
    },
  };
};

// ── Section presence (nav is data-driven; K3: absent, not empty) ──────────
export const availableSections = ({ standings, bracketRounds, schedule, stats }) => {
  const hasTables =
    Array.isArray(standings?.standings) && standings.standings.length > 0;
  const hasBracket = Array.isArray(bracketRounds) && bracketRounds.length > 0;
  const hasSchedule = schedule && schedule.rounds.length > 0;
  const hasStats =
    stats &&
    (stats.rating ||
      Object.values(stats.playerBoards || {}).some((rows) => rows.length > 0));
  const sections = [];
  if (hasTables || hasBracket || hasSchedule || hasStats) {
    sections.push(SECTIONS.OVERVIEW);
  }
  if (hasTables || hasBracket) sections.push(SECTIONS.STANDINGS);
  if (hasSchedule) sections.push(SECTIONS.SCHEDULE);
  if (hasStats) sections.push(SECTIONS.STATS);
  return sections;
};

// Pure-KO cup: bracket but no points table → nav label "Bracket" (K2).
export const standingsLabelIsBracket = ({ standings, bracketRounds }) => {
  const hasTables =
    Array.isArray(standings?.standings) && standings.standings.length > 0;
  return Array.isArray(bracketRounds) && bracketRounds.length > 0 && !hasTables;
};

// Unique promotion zones from standings rows (legend order = first
// appearance top-to-bottom). Zones are canonicalised to the UI-SPEC §1.5
// palette (competitionsZones.js): the BE ships its own palette/naming, the
// page renders the spec's — provider passthrough only for zones outside the
// spec's five (QA2 defect 3).
// Passthrough zones (outside the spec's five) whose BE `name` is not English:
// translate via the STABLE promotion id (BE PR #411 pins them as machine
// slugs) instead of shipping the BE wording. Today that is only `euq`, whose
// BE name is the test-pinned German "Titel-/Euro-Quali" (intent per PR #411
// F-12) — EN base here, the DE bundle translates it back to the BE wording.
// Unknown ids keep the BE `name` verbatim (existing behaviour).
const PASSTHROUGH_ZONE_TITLES = {
  euq: "UEFA qualification",
};

export const promotionsFromStandings = (standingsPayload) => {
  const seen = new Map();
  (standingsPayload?.standings || []).forEach((row) => {
    const p = canonicalPromotion(row?.promotion);
    if (p?.id && p?.color && !seen.has(p.id)) {
      const title = PASSTHROUGH_ZONE_TITLES[p.id];
      seen.set(p.id, title ? { ...p, name: title } : p);
    }
  });
  return Array.from(seen.values());
};

// ── Profile navigation (QA2 defect 2) ────────────────────────────────────────
// Default row action = profile navigation, resolved EXACTLY like the Bestand:
// players go to /search/player/<wyscout_id> (TOTW: TeamOfTheWeek.js viewProfile
// — the BE resolves TS→Wyscout via WSPlayer.thesports_id and ships
// `wyscout_id`), clubs go to /search/club/<id> (the Wyscout-keyed club profile
// route, e.g. TenderCard/TransferPoints). TheSports ids are NOT platform ids —
// a row without a resolvable platform id yields null and stays inert (TOTW's
// "no dead affordance" rule). The V1 competitions API does not ship these
// fields yet (BE follow-up finding); this resolver accepts the TOTW field
// names plus the CONTRACTS.md `platform_*` names so the links light up the
// moment the BE delivers either.
const platformIdOf = (...candidates) => {
  const hit = candidates.find((v) => v != null && v !== "");
  return hit != null ? hit : null;
};

export const profilePathFor = (payload) => {
  if (!payload) return null;
  const type = payload.type || null;
  const row = payload.row || payload;
  if (type !== "team") {
    // A bare entity (bracket team, standings row) is only a PLAYER when the
    // caller says so — top-level ids on team-shaped payloads stay club ids.
    const player = row.player || (type === "player" ? row : null);
    const playerId = platformIdOf(
      player && player.wyscout_id,
      player && player.platform_player_id,
      player ? row.platform_player_id : null,
    );
    if (playerId != null) return `/search/player/${playerId}`;
    if (type === "player" || row.player) return null; // player row w/o identity
  }
  const team = row.team || row.club || row;
  const clubId = platformIdOf(
    team && team.wyscout_id,
    team && team.ws_team_id,
    team && team.platform_club_id,
    row.platform_club_id,
  );
  return clubId != null ? `/search/club/${clubId}` : null;
};

// Affordance gate for the embeddables' `navigable` prop.
export const hasProfileTarget = (payload) => Boolean(profilePathFor(payload));
